import { useEffect, useRef, useState } from 'react'
import { Loader, LoaderOptions } from '@googlemaps/js-api-loader'
import { SERVICES_NEEDING_CONSENT, useCookieConsent } from 'ComponentLibrary'

type Services = {
    autocomplete: google.maps.places.AutocompleteService
    geocoder: google.maps.Geocoder
}

export const GOOGLE_API_LIBRARIES: LoaderOptions['libraries'] = ['places']
export const SUPPORTED_GOOGLE_LANGUAGES = ['de', 'en'] as const
export type SupportedGoogleLanguage = (typeof SUPPORTED_GOOGLE_LANGUAGES)[number]
export const DEFAULT_GOOGLE_LANGUAGE: SupportedGoogleLanguage = 'de'
export const BOOTSTRAP_URL_OPTIONS = {
    libraries: ['places' as const],
}
export const mapLocaleToGoogleMapsLanguage = (language: string = DEFAULT_GOOGLE_LANGUAGE): SupportedGoogleLanguage => {
    const normalizedLanguage = language.substring(0, 2).toLowerCase()

    return SUPPORTED_GOOGLE_LANGUAGES.find((supportedLanguage) => supportedLanguage === normalizedLanguage) ?? DEFAULT_GOOGLE_LANGUAGE
}
export const getGoogleLoaderOptions = (apiKey: string, language: string = DEFAULT_GOOGLE_LANGUAGE): LoaderOptions => ({
    apiKey,
    ...BOOTSTRAP_URL_OPTIONS,
    language: mapLocaleToGoogleMapsLanguage(language),
})

type Status = 'idle' | 'loading' | 'ready' | 'error'
export const useGoogleAPI = (apiKey: string, language: string = DEFAULT_GOOGLE_LANGUAGE) => {
    const normalizedLanguage = mapLocaleToGoogleMapsLanguage(language)
    const serviceRef = useRef<typeof window.google.maps>(undefined)
    const { isEnabled } = useCookieConsent(SERVICES_NEEDING_CONSENT.GOOGLE_MAPS)
    const [services, setServices] = useState<Services | null>(null)
    const [status, setStatus] = useState<Status>('idle')

    useEffect(() => {
        if (!isEnabled) {
            return // we stay in idle
        }

        setStatus('loading')
        const loader = new Loader(getGoogleLoaderOptions(apiKey, normalizedLanguage))

        loader
            .importLibrary('places')
            .then(() => {
                serviceRef.current = window.google.maps
                setServices({
                    autocomplete: new window.google.maps.places.AutocompleteService(),
                    geocoder: new window.google.maps.Geocoder(),
                })
                setStatus('ready')
            })
            .catch(() => {
                setStatus('error')
            })
    }, [apiKey, isEnabled, normalizedLanguage])

    return {
        autocompleteService: services?.autocomplete,
        geocoderService: services?.geocoder,
        status,
    }
}
